/**
 * Form validation utilities for SOW Generator
 */

/**
 * Get validation status for a form field
 * @param {Object} errors - Form errors object
 * @param {string} fieldName - Name of the field
 * @param {string} value - Current field value
 * @returns {string} - 'valid', 'error', or 'empty'
 */
export const getFieldValidationStatus = (errors, fieldName, value) => {
  const hasError = errors[fieldName];
  const isEmpty = !value || value.trim() === '';
  
  if (hasError) return 'error';
  if (isEmpty) return 'empty';
  return 'valid';
};

/**
 * Get character count color based on current length and limits
 * @param {number} current - Current character count
 * @param {number} min - Minimum required characters
 * @param {number} max - Maximum allowed characters
 * @returns {string} - 'error', 'warning', or 'success'
 */
export const getCharacterCountColor = (current, min, max) => {
  if (current < min) return 'error';
  if (current > max * 0.9) return 'warning';
  return 'success';
};

/**
 * Validate scoping notes and return status with message
 * @param {string} value - Scoping notes value
 * @param {number} minLength - Minimum required length (default: 50)
 * @param {number} maxLength - Maximum allowed length (default: 10000)
 * @param {boolean} isFileUploaded - Whether a file has been uploaded (makes text optional)
 * @returns {Object} - { status, message }
 */
export const validateScopingNotes = (value = '', minLength = 50, maxLength = 10000, isFileUploaded = false) => {
  const length = value.length;
  
  if (length === 0) {
    if (isFileUploaded) {
      return { status: 'valid', message: 'Scoping notes will be extracted from the uploaded file' };
    }
    return { status: 'empty', message: 'Enter scoping notes or upload a file below' };
  }
  
  if (length < minLength) {
    return { 
      status: 'error', 
      message: `Need at least ${minLength - length} more characters for detailed notes` 
    };
  }
  
  if (length > maxLength) {
    return { 
      status: 'error', 
      message: `Exceeds maximum by ${length - maxLength} characters` 
    };
  }
  
  return { 
    status: 'valid', 
    message: 'Good length for detailed scoping notes' 
  };
};

/**
 * Get quality level based on character count
 * @param {number} charCount - Current character count
 * @returns {string} - 'Basic', 'Moderate', or 'Detailed'
 */
export const getContentQualityLevel = (charCount) => {
  if (charCount >= 500) return 'Comprehensive';
  if (charCount >= 200) return 'Detailed';
  if (charCount >= 100) return 'Moderate';
  return 'Basic';
};

/**
 * Validation rules for form fields
 */
export const validationRules = {
  customerName: {
    required: 'Customer name is required',
    minLength: { value: 2, message: 'Customer name must be at least 2 characters' },
    maxLength: { value: 200, message: 'Customer name must be less than 200 characters' }
  },
  projectType: {
    required: 'Project type is required'
  },
  projectTitle: {
    maxLength: { value: 200, message: 'Project title must be less than 200 characters' }
  },
  estimatedDuration: {
    maxLength: { value: 100, message: 'Duration must be less than 100 characters' }
  },
  budgetRange: {
    maxLength: { value: 100, message: 'Budget range must be less than 100 characters' }
  },
  scopingNotes: {
    // Not required when a file is uploaded — validated at submit time
    maxLength: { value: 10000, message: 'Scoping notes must be less than 10,000 characters' }
  }
};